import { useState } from "react";
import api from "../lib/api.js";
import { useAuth } from "../lib/auth.jsx";
import { Callout, Field, Modal, useToast } from "./ui.jsx";

/**
 * ChangePassword lets the signed-in operator replace their own password.
 *
 * The current password is required, so a session left open on a shared
 * machine cannot be used to lock the real owner out.
 */
export default function ChangePassword({ onClose }) {
  const toast = useToast();
  const { session } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const tooShort = next.length > 0 && next.length < 12;
  const mismatch = repeat.length > 0 && repeat !== next;
  const unchanged = next.length > 0 && next === current;
  const valid = next.length >= 12 && next === repeat && !unchanged && current.length > 0;

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.changePassword(current, next);
      toast.success("Password changed", "Your other sessions have been signed out.");
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Change your password"
      hint={session?.email}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" form="change-password" disabled={busy || !valid}>
            {busy ? <span className="spinner" /> : null}
            Change password
          </button>
        </>
      }
    >
      <form id="change-password" onSubmit={submit}>
        {error && <Callout kind="warning">{error}</Callout>}

        <Field label="Current password" required>
          <input
            className="input"
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
          />
        </Field>

        <Field
          label="New password"
          required
          error={
            tooShort
              ? "At least twelve characters."
              : unchanged
              ? "The new password must differ from the current one."
              : null
          }
        >
          <input
            className="input"
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            required
          />
        </Field>

        <Field
          label="Repeat new password"
          required
          error={mismatch ? "The two entries do not match." : null}
        >
          <input
            className="input"
            type="password"
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
            autoComplete="new-password"
            required
          />
        </Field>

        <Callout kind="info">
          Every other session signed in as you will be ended. The session you are using
          now stays open.
        </Callout>
      </form>
    </Modal>
  );
}

/** ResetPassword is the administrator's recovery path for a locked-out account. */
export function ResetPassword({ email, onClose }) {
  const toast = useToast();
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.resetOperatorPassword(email, next);
      toast.success(`Password reset for ${email}`, "Their sessions have been signed out.");
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Reset the password for ${email}`}
      hint="Use this when somebody is locked out or has forgotten their password"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" form="reset-password" disabled={busy || next.length < 12}>
            {busy ? <span className="spinner" /> : null}
            Reset password
          </button>
        </>
      }
    >
      <form id="reset-password" onSubmit={submit}>
        {error && <Callout kind="warning">{error}</Callout>}

        <Field
          label="New password"
          required
          hint="Twelve characters minimum. Give it to them over a channel you trust, and ask them to change it."
        >
          <input
            className="input"
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            required
          />
        </Field>

        <Callout kind="warning">
          This clears their lockout and ends every session they currently have open.
        </Callout>
      </form>
    </Modal>
  );
}
